/**
 * Anti spam helper for powermail forms.
 *
 * Adds and fills four hidden fields on every powermail form:
 *   field[__jsc] - token derived from the form uid, proves that JavaScript ran
 *   field[__jst] - milliseconds since the form was first seen on this page
 *   field[__int] - token, only written after the first trusted user interaction
 *   field[__url] - honeypot, a real text input that has to stay empty
 *
 * They are validated server side by
 * HieblMedia\PowermailExtras\Domain\Validator\SpamShield\JavaScriptTimeMethod.
 *
 * The fields are created here instead of in a Fluid partial on purpose: a bot
 * that does not execute JavaScript never submits them, and no partial override
 * (and therefore no partialRootPaths entry) is needed to activate the check.
 *
 * On top of that the honeypot field of powermail is taken out of the tab order
 * and hidden from screen readers, so real visitors cannot fill it by accident.
 *
 * The honeypot and the interaction check follow the guard of the project
 * hofmann_api_frontent_base (src/templateLoaderApi.js, hofmannApiLoader.guard).
 */
(function () {
    'use strict';

    var FIELD_JAVASCRIPT = '__jsc';
    var FIELD_ELAPSED = '__jst';
    var FIELD_INTERACTION = '__int';

    // Honeypot of this extension, created below
    var FIELD_HONEYPOT_JS = '__url';

    // Honeypot of powermail itself, rendered by its Fluid partial
    var FIELD_HONEYPOT = '__hp';

    /**
     * The first one of these events that a visitor triggers on a form counts as
     * the proof that a human is filling it in.
     */
    var INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'focusin'];

    // form uid => timestamp of the first time the form was seen on this page
    var startTimes = {};

    // form uid => timestamp of the first trusted interaction with the form
    var interactionTimes = {};

    /**
     * Must stay in sync with JavaScriptTimeMethod::getJavaScriptToken()
     */
    function token(formUid) {
        return (formUid * 7919 + 104729).toString(36);
    }

    /**
     * Must stay in sync with JavaScriptTimeMethod::getInteractionToken()
     */
    function interactionToken(formUid) {
        return (formUid * 104729 + 7919).toString(36);
    }

    /**
     * The plugin namespace ("tx_powermail_pi1") is read from the form itself
     * instead of being hardcoded.
     *
     * @return {?string} null if the form does not look like a powermail form
     */
    function pluginNamespace(form) {
        var uidField = form.querySelector('input.powermail_form_uid');
        var match = uidField && uidField.name.match(/^(.+?)\[mail]\[form]$/);

        return match ? match[1] : null;
    }

    function formUid(form) {
        var uidField = form.querySelector('input.powermail_form_uid');

        return uidField ? (parseInt(uidField.value, 10) || 0) : 0;
    }

    function fieldName(namespace, marker) {
        return namespace + '[field][' + marker + ']';
    }

    function hiddenField(form, namespace, marker) {
        var name = fieldName(namespace, marker);
        var field = form.querySelector('input[name="' + name + '"]');

        if (!field) {
            field = document.createElement('input');
            field.type = 'hidden';
            field.name = name;
            form.appendChild(field);
        }

        return field;
    }

    /**
     * A real text input: reachable for a bot that fills in every field, but off
     * screen, unreachable by keyboard and ignored by screen readers.
     *
     * The inline style is set through the CSSOM instead of a style attribute in
     * the markup, so it passes the Content Security Policy of the site.
     */
    function honeypotField(form, namespace, uid) {
        var name = fieldName(namespace, FIELD_HONEYPOT_JS);

        if (form.querySelector('input[name="' + name + '"]')) {
            return;
        }

        var honeypot = document.createElement('input');
        honeypot.type = 'text';
        honeypot.name = name;
        honeypot.value = '';
        honeypot.tabIndex = -1;
        honeypot.setAttribute('autocomplete', 'new-powermail-url-' + uid);
        honeypot.setAttribute('aria-hidden', 'true');
        honeypot.style.cssText = 'position:absolute !important;left:-9999px !important;' +
            'width:1px !important;height:1px !important;opacity:0 !important;' +
            'padding:0 !important;border:0 !important;overflow:hidden !important;';

        form.appendChild(honeypot);
    }

    function hardenHoneypot(form, namespace, uid) {
        var honeypot = form.querySelector('input[name="' + fieldName(namespace, FIELD_HONEYPOT) + '"]');

        if (!honeypot || honeypot.getAttribute('tabindex') === '-1') {
            return;
        }

        honeypot.setAttribute('tabindex', '-1');
        honeypot.setAttribute('autocomplete', 'new-powermail-hp-' + uid);

        // The wrapper holds the label as well, so hide it as a whole
        if (honeypot.parentNode && honeypot.parentNode.nodeType === 1) {
            honeypot.parentNode.setAttribute('aria-hidden', 'true');
        } else {
            honeypot.setAttribute('aria-hidden', 'true');
        }
    }

    function update(form) {
        var namespace = pluginNamespace(form);

        if (!namespace) {
            return;
        }

        var uid = formUid(form);

        if (!startTimes[uid]) {
            startTimes[uid] = Date.now();
        }

        hardenHoneypot(form, namespace, uid);
        honeypotField(form, namespace, uid);
        hiddenField(form, namespace, FIELD_JAVASCRIPT).value = token(uid);
        hiddenField(form, namespace, FIELD_ELAPSED).value = String(Date.now() - startTimes[uid]);
        hiddenField(form, namespace, FIELD_INTERACTION).value = interactionTimes[uid] ? interactionToken(uid) : '';
    }

    function updateAll() {
        var forms = document.querySelectorAll('form.powermail_form');

        for (var i = 0; i < forms.length; i++) {
            update(forms[i]);
        }
    }

    function powermailForm(event) {
        var target = event.target;

        if (!target || !target.closest) {
            return null;
        }

        return target.closest('form.powermail_form');
    }

    /**
     * Remembers the first interaction of a visitor with a form. Events
     * dispatched by a script carry isTrusted === false and are ignored, so
     * filling in the form from code alone does not unlock the token.
     */
    function onInteraction(event) {
        if (event && event.isTrusted === false) {
            return;
        }

        var form = powermailForm(event);

        if (!form) {
            return;
        }

        var uid = formUid(form);

        if (!interactionTimes[uid]) {
            interactionTimes[uid] = Date.now();
            update(form);
        }
    }

    /**
     * Delegating on the document keeps the listeners working after the form
     * markup has been replaced, for example after a validation error.
     */
    for (var i = 0; i < INTERACTION_EVENTS.length; i++) {
        document.addEventListener(INTERACTION_EVENTS[i], onInteraction, {capture: true, passive: true});
    }

    /**
     * Capturing on the document, so the values are written before the submit
     * handler of powermail serializes the form for its AJAX request
     * (settings.misc.ajaxSubmit = 1). Delegating also survives the form markup
     * being replaced after a validation error.
     */
    document.addEventListener('submit', function (event) {
        var form = event.target;

        if (form && form.classList && form.classList.contains('powermail_form')) {
            update(form);
        }
    }, true);

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', updateAll);
    } else {
        updateAll();
    }
})();
